import StrategyWorkflowService from "./StrategyWorkflowService";
import AutonomousStrategyWorkflowService from "./AutonomousStrategyWorkflowService";
import workflowRegistry from "./WorkflowRegistry";
import modularWorkflowEngine from "./ModularWorkflowEngine";

// REMOVED: Server-side require() breaks webpack compilation
// Client-side ALWAYS uses fetch API in ModularWorkflowEngine to load registry

class StrategyWorkflowManager {
  constructor() {
    this.activeWorkflows = new Map();
    this.workflowCallbacks = new Map();
    this.isExecuting = false;
  }

  // REGISTRY-DRIVEN: Get workflow steps from service based on workflow config
  // Decision Call returns optimized 2-step workflow, Full Strategy returns complete 4-step workflow
  // Feature Enablement returns feature-specific steps
  async getWorkflowSteps(workflowId = null, featureId = null) {
    console.log("🔬 getWorkflowSteps CALLED:", { workflowId, featureId });
    
    if (!workflowId) {
      throw new Error('CRITICAL: workflowId is required to determine workflow steps');
    }

    const workflow = workflowRegistry.getWorkflow(workflowId);
    if (!workflow) {
      throw new Error(`CRITICAL: Workflow not found in registry: ${workflowId}`);
    }

    // CRITICAL FIX: For feature_enablement workflows, look up featureId from active workflow params
    // This ensures onStepStart/onStepComplete callbacks get correct feature-specific steps
    // even when they don't explicitly pass featureId
    let resolvedFeatureId = featureId;
    if (workflow.isFeatureEnablement && !resolvedFeatureId) {
      // Find the running feature_enablement workflow and get its featureId from params
      const runningFeatureWorkflow = Array.from(this.activeWorkflows.entries()).find(
        ([execId, wf]) => wf.workflowId === 'feature_enablement' && wf.status === 'running'
      );
      if (runningFeatureWorkflow) {
        resolvedFeatureId = runningFeatureWorkflow[1].params?.featureId;
        console.log(`📊 getWorkflowSteps: Resolved featureId from active workflow params: ${resolvedFeatureId}`);
      }
    }

    // FEATURE ENABLEMENT MODE: Use feature-specific steps from templates
    if (workflow.isFeatureEnablement && resolvedFeatureId) {
      const templates = workflowRegistry.getFeatureEnablementTemplates(resolvedFeatureId);
      console.log(`📊 getWorkflowSteps: feature_enablement for ${resolvedFeatureId}, ${templates.steps.length} custom steps`);
      
      // Convert template steps to workflow step format
      const featureSteps = templates.steps.map((step, index) => ({
        id: step.id,
        name: step.label,
        title: step.label, // Add both for UI compatibility
        status: "pending",
        stepNumber: index + 1,
      }));
      
      console.log("🔬 getWorkflowSteps RETURNING (feature_enablement):", { 
        featureId: resolvedFeatureId, 
        stepCount: featureSteps.length, 
        steps: featureSteps.map(s => ({ id: s.id, title: s.title }))
      });
      
      return featureSteps;
    }
    
    // REGISTRY-DRIVEN: Check if workflow uses modular engine (has enabledFeatures)
    const usesModularEngine = workflow?.uiComponents?.enabledFeatures?.length > 0;
    
    if (usesModularEngine) {
      try {
        const workflowConfig = await modularWorkflowEngine.createWorkflowConfig(workflowId, workflowRegistry);
        
        if (!workflowConfig || !workflowConfig.executionPlan) {
          throw new Error(`CRITICAL: Failed to create workflow config for ${workflowId} - execution plan is null`);
        }
        
        const autonomousService = new AutonomousStrategyWorkflowService();
        const allSteps = autonomousService.getSteps();
        const skipSteps = workflowConfig.executionPlan.skipSteps || [];
        
        // Filter out skipped steps
        const optimizedSteps = allSteps.filter((step) => 
          !skipSteps.includes(step.id)
        );
        
        if (optimizedSteps.length === 0) {
          throw new Error(`CRITICAL: Workflow ${workflowId} has no steps after filtering - execution plan may be invalid`);
        }
        
        console.log(`📊 getWorkflowSteps: ${workflowId} optimized from ${allSteps.length} to ${optimizedSteps.length} steps`);
        return optimizedSteps;
      } catch (error) {
        console.error(`❌ CRITICAL ERROR in getWorkflowSteps for ${workflowId}:`, error);
        throw error; // Propagate error instead of falling back
      }
    }
    
    // REGISTRY-DRIVEN: Check if autonomous workflow (but NOT modular)
    const isAutonomous = workflow?.uiComponents?.chatPanel?.autonomousMode === true;
    
    if (!isAutonomous) {
      // Guided workflow - use StrategyWorkflowService
      const strategyService = new StrategyWorkflowService();
      return strategyService.getSteps();
    }
    
    // Only reach here for non-modular autonomous workflows (legacy full plan)
    console.log(`📊 getWorkflowSteps: ${workflowId} using legacy full autonomous workflow (4 steps)`);
    const autonomousService = new AutonomousStrategyWorkflowService();
    return autonomousService.getSteps();
  }

  // Start a new strategy creation workflow
  async startStrategyWorkflow(workflowId, params, callbacks) {
    console.log(`🚀🚀🚀 TRACE [A] StrategyWorkflowManager.startStrategyWorkflow: ${workflowId}`);
    console.log(`🚀 StrategyWorkflowManager: Starting workflow ${workflowId}`);

    // Clean up any stale workflows before starting new one
    this.cleanupStaleWorkflows();

    // Check for already running workflows with same design challenge to prevent duplicates
    // Only block if there's a VERY recent running workflow (within last 500ms) to prevent race conditions
    const now = new Date();
    const existingWorkflow = Array.from(this.activeWorkflows.entries()).find(
      ([executionId, workflow]) =>
        workflow.status === "running" &&
        workflow.params?.designChallenge === params?.designChallenge &&
        executionId !== workflowId && // Don't block self
        now - workflow.startTime < 500, // Reduced to 500ms for tighter control
    );

    if (existingWorkflow) {
      const [existingExecutionId, existingWorkflowData] = existingWorkflow;
      console.log(
        "⚠️ StrategyWorkflowManager: Blocking duplicate workflow for same design challenge",
        {
          existingExecutionId,
          newExecutionId: workflowId,
          timeSinceStart: (now - existingWorkflowData.startTime) / 1000,
        },
      );
      throw new Error(`Workflow already running for this design challenge`);
    }

    // Validate workflow exists
    const workflow = workflowRegistry.getWorkflow(workflowId);
    if (!workflow) {
      throw new Error(`Workflow ${workflowId} not found`);
    }

    // Create unique execution ID
    const executionId = `${workflowId}_${Date.now()}`;

    // Store callbacks for this execution
    // CRITICAL FIX: Clone the callbacks object to prevent React re-renders from mutating
    // the stored reference. Without this, when React rebuilds callbacks on re-render or
    // when the component unmounts (user navigates away), the stored callbacks become stale/empty.
    const clonedCallbacks = {
      onStepStart: callbacks?.onStepStart,
      onStepComplete: callbacks?.onStepComplete,
      onComplete: callbacks?.onComplete,
      onError: callbacks?.onError,
      onChatMessage: callbacks?.onChatMessage,
      onProgressUpdate: callbacks?.onProgressUpdate,
    };
    this.workflowCallbacks.set(executionId, clonedCallbacks);
    console.log("🔒 CALLBACKS CLONED: Stored independent copy of callbacks for execution", executionId);

    // Mark workflow as active
    this.activeWorkflows.set(executionId, {
      workflowId,
      status: "running",
      startTime: new Date(),
      params,
      messagesInitialized: false, // Track message initialization per execution
    });

    try {
      // Execute the workflow
      const result = await this.executeWorkflow(
        executionId,
        workflowId,
        params,
      );

      // Mark as completed
      this.activeWorkflows.set(executionId, {
        ...this.activeWorkflows.get(executionId),
        status: "completed",
        endTime: new Date(),
        result,
      });

      return { executionId, result };
    } catch (error) {
      // Mark as failed
      this.activeWorkflows.set(executionId, {
        ...this.activeWorkflows.get(executionId),
        status: "failed",
        endTime: new Date(),
        error: error.message,
      });

      throw error;
    } finally {
      this.isExecuting = false;
    }
  }

  // REGISTRY-DRIVEN: Execute workflow based on registry config
  async executeWorkflow(executionId, workflowId, params) {
    console.log("═══════════════════════════════════════════════════════════════");
    console.log(`🎯 FEATURE ENABLEMENT TRACE [7/10]: StrategyWorkflowManager.executeWorkflow`);
    console.log(`🎯 workflowId: ${workflowId}`);
    console.log(`🎯 executionId: ${executionId}`);
    console.log(`🎯 params.triggeredManually: ${params.triggeredManually}`);
    console.log(`🎯 params.featureId: ${params.featureId}`);
    console.log(`🎯 params.executionPlan: ${JSON.stringify(params.executionPlan)}`);
    console.log("═══════════════════════════════════════════════════════════════");
    
    const callbacks = this.workflowCallbacks.get(executionId);
    const workflow = workflowRegistry.getWorkflow(workflowId);
    
    if (!workflow) {
      console.error(`❌ Workflow ${workflowId} not found in registry!`);
      throw new Error(`Workflow ${workflowId} not found in registry`);
    }

    console.log(`🎯 Workflow found in registry:`, {
      name: workflow.name,
      isFeatureEnablement: workflow.isFeatureEnablement,
      autonomousMode: workflow.uiComponents?.chatPanel?.autonomousMode,
      type: workflow.uiComponents?.chatPanel?.type
    });

    // REGISTRY-DRIVEN: Check if autonomous workflow
    const isAutonomous = workflow.uiComponents?.chatPanel?.autonomousMode === true;
    
    // DEBUG: Check what getInitialPlanWorkflows returns
    const initialPlanWorkflows = workflowRegistry.getInitialPlanWorkflows(workflowId);
    console.log(`🔍🔍🔍 DEBUG getInitialPlanWorkflows(${workflowId}):`, {
      length: initialPlanWorkflows.length,
      featureIds: initialPlanWorkflows.map(fw => fw.featureId),
      hasAutoRunFeatures: !!workflow.uiComponents?.autoRunFeatures,
      autoRunFeatures: workflow.uiComponents?.autoRunFeatures,
    });
    
    // NEW: Check if we should use the Feature Workflow Sequencer
    // Feature Sequencer is used for initial plan creation (quick_plan / autonomous_design_strategy)
    // but NOT for feature enablement (triggeredManually=true) or already-started workflows
    const useFeatureSequencer = isAutonomous && 
      !params.triggeredManually && 
      !params.featureId &&
      (workflowId === 'quick_plan' || workflowId === 'autonomous_design_strategy') &&
      initialPlanWorkflows.length > 0;
    
    // NEW: Check if this is a manual feature enablement (user added feature via + button)
    // These should use the feature's OWN workflow service, not the legacy autonomous service
    const useFeatureOwnedWorkflow = params.triggeredManually && 
      params.featureId && 
      workflowRegistry.getFeatureWorkflowService(params.featureId);
    
    console.log(`🎯 isAutonomous=${isAutonomous}, useFeatureSequencer=${useFeatureSequencer}, useFeatureOwnedWorkflow=${!!useFeatureOwnedWorkflow}`);
    console.log(`🎯 will call: ${useFeatureOwnedWorkflow ? 'executeFeatureOwnedWorkflow' : (useFeatureSequencer ? 'executeSequencedFeatureWorkflows' : (isAutonomous ? 'executeAutonomousDesignStrategyWorkflow' : 'executeDesignStrategyWorkflow'))}`);
    
    if (useFeatureOwnedWorkflow) {
      // NEW: Route manual feature enablement to the feature's own workflow service
      return await this.executeFeatureOwnedWorkflow(
        executionId,
        workflowId,
        params,
        callbacks,
      );
    } else if (useFeatureSequencer) {
      // NEW: Use the Feature Workflow Sequencer for initial plan creation
      return await this.executeSequencedFeatureWorkflows(
        executionId,
        workflowId,
        params,
        callbacks,
      );
    } else if (isAutonomous) {
      return await this.executeAutonomousDesignStrategyWorkflow(
        executionId,
        workflowId,
        params,
        callbacks,
      );
    } else {
      return await this.executeDesignStrategyWorkflow(
        executionId,
        workflowId,
        params,
        callbacks,
      );
    }
  }

  // NEW: Execute a feature's own workflow service for manual feature enablement
  async executeFeatureOwnedWorkflow(executionId, workflowId, params, callbacks) {
    console.log("═══════════════════════════════════════════════════════════════");
    console.log(`🎯 FEATURE OWNED WORKFLOW: Executing ${params.featureId} via its own service`);
    console.log(`🎯 executionId: ${executionId}`);
    console.log(`🎯 designChallenge preview: ${params.designChallenge?.substring(0, 100)}`);
    console.log("═══════════════════════════════════════════════════════════════");

    const { featureId, designChallenge, csvData, briefData, strategyData, planId } = params;
    
    // NOTE: step_by_step_view is now handled earlier in EmbeddedChatPanel.triggerManualFeatureWorkflow
    // It returns early with no workflow execution, so this code path won't be reached for that feature
    
    // Get the feature's own workflow service
    const featureService = workflowRegistry.getFeatureWorkflowService(featureId);
    
    if (!featureService) {
      console.error(`❌ No workflow service found for feature ${featureId}`);
      throw new Error(`No workflow service found for feature ${featureId}`);
    }

    console.log(`✅ Found workflow service for ${featureId}:`, typeof featureService);

    // Build service-specific callbacks that update the progress UI
    const serviceCallbacks = {
      onStepStart: async (stepIndex, step) => {
        console.log(`🚀 Feature ${featureId}: Step ${stepIndex} starting:`, step.title || step.id);
        
        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "running" });
        }
        
        // Update progress UI in chat
        if (callbacks?.onChatMessage) {
          const steps = featureService.getSteps ? featureService.getSteps() : [];
          const updatedSteps = steps.map((s, idx) => ({
            ...s,
            status: idx < stepIndex ? "completed" : idx === stepIndex ? "running" : "pending"
          }));
          
          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: updatedSteps,
            currentStep: stepIndex,
            sender: "bot",
            featureId: featureId,
            progressId: featureId,
            isFeatureEnablement: true,
          });
        }
      },
      
      onStepComplete: async (stepIndex, step, results) => {
        console.log(`✅ Feature ${featureId}: Step ${stepIndex} completed:`, step.title || step.id);
        
        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "completed" });
        }
      },
      
      onComplete: async (finalResults) => {
        console.log(`✅ Feature ${featureId}: Workflow completed successfully`);
        
        // Mark all steps as completed in UI
        if (callbacks?.onChatMessage) {
          const steps = featureService.getSteps ? featureService.getSteps() : [];
          const completedSteps = steps.map(s => ({ ...s, status: "completed" }));
          
          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: completedSteps,
            currentStep: steps.length - 1,
            sender: "bot",
            featureId: featureId,
            progressId: featureId,
            isFeatureEnablement: true,
            isComplete: true,
          });
        }
        
        // Forward to original completion callback
        if (callbacks?.onComplete) {
          callbacks.onComplete({
            ...finalResults,
            featureId,
            triggeredManually: true,
          });
        }
      },
      
      onError: (error) => {
        console.error(`❌ Feature ${featureId}: Workflow failed:`, error);
        if (callbacks?.onError) {
          callbacks.onError(error);
        }
      },
    };

    // Build params for the feature service
    const serviceParams = {
      designChallenge,
      briefData: briefData || {},
      strategyData: strategyData || {},
      csvData: csvData || { tools: [], methods: [], programsOfWork: [] },
      planId,
    };

    try {
      // Execute the feature's own workflow
      const results = await featureService.executeWorkflow(serviceParams, serviceCallbacks);
      return results;
    } catch (error) {
      console.error(`❌ Feature ${featureId} workflow execution failed:`, error);
      serviceCallbacks.onError(error);
      throw error;
    }
  }

  // Execute design strategy workflow
  async executeDesignStrategyWorkflow(executionId, workflowId, params, callbacks) {
    const { designChallenge, programsOfWork, methods, tools, deliverables } =
      params;

    // Create strategy service instance
    const strategyService = new StrategyWorkflowService();

    // Enhanced callbacks that include workflow management and messaging
    const enhancedCallbacks = {
      onStepUpdate: (stepIndex, step) => {
        // Update internal state
        const workflowState = this.activeWorkflows.get(executionId);
        if (workflowState) {
          workflowState.currentStep = stepIndex;
          workflowState.stepStatus = step.status;
        }

        // Forward to original callback
        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, step);
        }
      },

      onStepStart: async (stepIndex, step) => {
        console.log(
          `🚀 StrategyWorkflowManager: Step ${stepIndex} starting:`,
          step.title,
        );

        // Update step status and progress UI
        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "running" });
        }

        // Update progress UI in chat for steps after first (first step UI is handled in message flow)
        if (stepIndex > 0 && callbacks?.onChatMessage) {
          // CRITICAL FIX: Pass featureId directly to get correct feature-specific steps
          const updatedSteps = await this.getWorkflowSteps(workflowId, params.featureId);
          updatedSteps[stepIndex] = {
            ...updatedSteps[stepIndex],
            status: "running",
          };

          // Mark all previous steps as completed
          for (let i = 0; i < stepIndex; i++) {
            updatedSteps[i] = { ...updatedSteps[i], status: "completed" };
          }

          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: updatedSteps,
            currentStep: stepIndex,
            sender: "bot",
            // CRITICAL FIX: Include featureId/progressId so FloatingChatbot can find and UPDATE
            // existing progress UI instead of adding new ones
            featureId: params.featureId,
            progressId: params.featureId || 'default',
            isFeatureEnablement: !!params.featureId,
          });
        }
      },

      onStepComplete: async (stepIndex, step, results) => {
        console.log(
          `✅ StrategyWorkflowManager: Step ${stepIndex} completed:`,
          step.title,
        );

        // Update step status and progress UI
        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "completed" });
        }

        // Update progress UI in chat with completed status
        if (callbacks?.onChatMessage) {
          // CRITICAL FIX: Pass featureId directly to get correct feature-specific steps
          const updatedSteps = await this.getWorkflowSteps(workflowId, params.featureId);
          updatedSteps[stepIndex] = {
            ...updatedSteps[stepIndex],
            status: "completed",
          };

          // Ensure all previous steps are also marked completed
          for (let i = 0; i <= stepIndex; i++) {
            if (updatedSteps[i].status !== "error") {
              updatedSteps[i] = { ...updatedSteps[i], status: "completed" };
            }
          }

          // FIX: Cap currentStep at last valid index to prevent empty step appearing
          // When last step completes, don't increment beyond the array bounds
          const nextStep = Math.min(stepIndex + 1, updatedSteps.length - 1);
          
          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: updatedSteps,
            currentStep: nextStep,
            sender: "bot",
            // CRITICAL FIX: Include featureId/progressId so FloatingChatbot can find and UPDATE
            // existing progress UI instead of adding new ones
            featureId: params.featureId,
            progressId: params.featureId || 'default',
            isFeatureEnablement: !!params.featureId,
          });
        }
      },

      onComplete: async (workflowResults) => {
        console.log(
          "🎉 StrategyWorkflowManager: Workflow completed - processing results",
        );
        console.log("🔍 Raw workflow results received:", {
          hasResults: !!workflowResults,
          hasPrograms: !!workflowResults?.finalProgramSequence,
          programsCount: workflowResults?.finalProgramSequence?.length || 0,
          hasShortSummary: !!workflowResults?.shortSummary,
          resultKeys: workflowResults ? Object.keys(workflowResults) : [],
        });

        // Process and validate results
        const processedResults =
          await this.processWorkflowResults(workflowResults);

        // CRITICAL: Validate programs exist before completion
        if (
          !processedResults.finalProgramSequence ||
          processedResults.finalProgramSequence.length === 0
        ) {
          console.error("❌ No programs in completed workflow results");
          console.error("❌ Processed results debug:", {
            originalHasPrograms: !!workflowResults?.finalProgramSequence,
            originalProgramsCount:
              workflowResults?.finalProgramSequence?.length || 0,
            processedHasPrograms: !!processedResults?.finalProgramSequence,
            processedProgramsCount:
              processedResults?.finalProgramSequence?.length || 0,
          });
          throw new Error("Workflow completed but no programs were generated");
        }

        console.log("✅ Workflow completion validation passed:", {
          programsCount: processedResults.finalProgramSequence.length,
          hasShortSummary: !!processedResults.shortSummary,
          programNames: processedResults.finalProgramSequence
            .map((p) => p.program_of_work || p.name)
            .slice(0, 3),
        });

        // Send processing message (not completion yet)
        if (callbacks?.onChatMessage) {
          callbacks.onChatMessage({
            text: `I've created a comprehensive strategic vision that captures the essence and strategic thinking to solve your design challenge.
            **What's next?**
              - **Refine & Customize:** Tweak the strategy, plan, tools via prompts or visual edits.
              - **Master Prompting:** Use the "chat" to discuss and learn more about your plan without making edits. Use clear, detailed, and iterative prompts for best results.`,
            sender: "bot",
            isHtml: false,
          });
        }

        // CRITICAL FIX: Forward ALL workflow data to the component action handler
        // Ensure data reaches ComponentActionHandler.handleStrategyCreationComplete
        if (callbacks?.onComplete) {
          const enhancedResults = {
            // Include ALL original workflow data
            ...workflowResults,
            // Include processed results
            ...processedResults,
            // CRITICAL: Ensure these specific fields are present for save
            selectedPrograms:
              processedResults.selectedPrograms ||
              workflowResults.selectedPrograms ||
              [],
            finalProgramSequence:
              processedResults.finalProgramSequence ||
              workflowResults.finalProgramSequence ||
              [],
            shortSummary:
              processedResults.shortSummary ||
              workflowResults.shortSummary ||
              "",
            problemStatement:
              processedResults.problemStatement ||
              workflowResults.problemStatement ||
              "",
            goalStatement:
              processedResults.goalStatement ||
              workflowResults.goalStatement ||
              "",
            targetStatement:
              processedResults.targetStatement ||
              workflowResults.targetStatement ||
              "",
            objectives:
              processedResults.objectives || workflowResults.objectives || [],
            deliverables:
              processedResults.deliverables ||
              workflowResults.deliverables ||
              [],
            metrics: processedResults.metrics || workflowResults.metrics || [],
            stakeholders:
              processedResults.stakeholders ||
              workflowResults.stakeholders ||
              [],
            totalDuration:
              processedResults.totalDuration ||
              workflowResults.totalDuration ||
              0,
            // CRITICAL FIX: Add experienceDesignPrinciples
            experienceDesignPrinciples:
              processedResults.experienceDesignPrinciples ||
              workflowResults.experienceDesignPrinciples ||
              "",
            // NOTE: onSaveComplete/onSaveError removed - conversation linking now handled directly in ComponentActionHandler
          };

          console.log(
            "🚀 Forwarding enhanced results to ComponentActionHandler:",
            {
              hasSelectedPrograms: !!enhancedResults.selectedPrograms,
              selectedProgramsCount:
                enhancedResults.selectedPrograms?.length || 0,
              hasFinalProgramSequence: !!enhancedResults.finalProgramSequence,
              finalProgramSequenceCount:
                enhancedResults.finalProgramSequence?.length || 0,
              hasShortSummary: !!enhancedResults.shortSummary,
              dataSize: JSON.stringify(enhancedResults).length,
            },
          );

          callbacks.onComplete(enhancedResults);
        } else {
          console.error(
            "❌ No onComplete callback found - data will not reach ComponentActionHandler",
          );
        }

        return processedResults;
      },

      onError: (error) => {
        console.error("❌ StrategyWorkflowManager: Workflow failed:", error);

        // Send error message to chat
        if (callbacks?.onChatMessage) {
          callbacks.onChatMessage({
            text: "⚠️ I encountered an issue while creating your strategy. Let me try again or you can refresh the page.",
            sender: "bot",
            isHtml: false,
          });
        }

        // Forward to original callback
        if (callbacks?.onError) {
          callbacks.onError(error);
        }
      },
    };

    // Send initial messages only - workflow UI will be initialized when first step starts
    // FIX: Skip initial messages for feature enablement (continueConversation=true) to prevent duplicates
    if (callbacks?.onChatMessage && !params.continueConversation) {
      const workflowState = this.activeWorkflows.get(executionId);

      // Only initialize messages if not already done for this specific execution
      if (workflowState && !workflowState.messagesInitialized) {
        console.log(
          `📝 StrategyWorkflowManager: Initializing messages for execution ${executionId}`,
        );

        // Mark messages as initialized to prevent duplicates
        workflowState.messagesInitialized = true;
        this.activeWorkflows.set(executionId, workflowState);

        // Send initial messages and progress UI in proper order
        const messageQueue = [
          {
            text: designChallenge,
            sender: "user",
            isHtml: false,
          },
          {
            text: "🚀 Creating your design strategy using AI analysis and our complete database of programs and tools...",
            sender: "bot",
            isHtml: false,
          },
        ];

        // Send messages with UI readiness check, then progress UI
        this.sendMessagesWithProgressUI(
          callbacks.onChatMessage,
          messageQueue,
          executionId,
        );
      } else {
        console.log(
          `⚠️ StrategyWorkflowManager: Skipping message initialization for execution ${executionId} (already initialized or no state)`,
        );
      }
    } else if (params.continueConversation) {
      console.log(
        `⏭️ StrategyWorkflowManager: Skipping initial messages for ${executionId} (continueConversation=true, feature enablement mode)`,
      );
    }

    // Execute the strategy workflow
    return await strategyService.executeWorkflow(
      designChallenge,
      programsOfWork,
      methods,
      tools,
      deliverables,
      enhancedCallbacks,
    );
  }

  // Process and enhance workflow results
  async processWorkflowResults(workflowResults) {
    console.log("🔧 StrategyWorkflowManager: Processing workflow results");

    // Validate essential data
    if (
      !workflowResults.finalProgramSequence ||
      workflowResults.finalProgramSequence.length === 0
    ) {
      throw new Error("Invalid workflow results: No programs generated");
    }

    // Calculate proper total duration
    const calculatedTotalDuration = workflowResults.finalProgramSequence.reduce(
      (sum, program) => {
        return sum + (program.totalProgramLength || program.duration || 0);
      },
      0,
    );

    // Enhanced results with metadata
    const enhancedResults = {
      ...workflowResults,
      totalDuration: calculatedTotalDuration,
      metadata: {
        processedAt: new Date().toISOString(),
        programCount: workflowResults.finalProgramSequence.length,
        validationPassed: true,
      },
      // Prepare data specifically for UI updates
      uiUpdate: {
        shortSummary: workflowResults.shortSummary,
        problemStatement: workflowResults.problemStatement,
        goalStatement: workflowResults.goalStatement,
        targetStatement: workflowResults.targetStatement,
        objectives: workflowResults.objectives,
        deliverables: workflowResults.deliverables,
        metrics: workflowResults.metrics,
        selectedPrograms: workflowResults.selectedPrograms,
        finalProgramSequence: workflowResults.finalProgramSequence,
        stakeholders: workflowResults.stakeholders,
        totalDuration: calculatedTotalDuration,
      },
    };

    return enhancedResults;
  }

  // Get workflow status
  getWorkflowStatus(executionId) {
    return this.activeWorkflows.get(executionId);
  }

  // Get all active workflows
  getActiveWorkflows() {
    return Array.from(this.activeWorkflows.entries()).map(([id, workflow]) => ({
      executionId: id,
      ...workflow,
    }));
  }

  // Cancel a workflow
  cancelWorkflow(executionId) {
    const workflow = this.activeWorkflows.get(executionId);
    if (workflow && workflow.status === "running") {
      this.activeWorkflows.set(executionId, {
        ...workflow,
        status: "cancelled",
        endTime: new Date(),
      });

      // Clean up callbacks
      this.workflowCallbacks.delete(executionId);

      return true;
    }
    return false;
  }

  // Clean up completed workflows (optional, for memory management)
  cleanupCompletedWorkflows(olderThanHours = 24) {
    const cutoffTime = new Date(Date.now() - olderThanHours * 60 * 60 * 1000);

    for (const [executionId, workflow] of this.activeWorkflows.entries()) {
      if (
        workflow.status !== "running" &&
        workflow.endTime &&
        workflow.endTime < cutoffTime
      ) {
        this.activeWorkflows.delete(executionId);
        this.workflowCallbacks.delete(executionId);
      }
    }
  }

  // Execute autonomous design strategy workflow
  async executeAutonomousDesignStrategyWorkflow(
    executionId,
    workflowId,
    params,
    callbacks,
  ) {
    console.log("═══════════════════════════════════════════════════════════════");
    console.log(`🎯 FEATURE ENABLEMENT TRACE [8/10]: executeAutonomousDesignStrategyWorkflow ENTERED`);
    console.log(`🎯 workflowId: ${workflowId}`);
    console.log(`🎯 executionId: ${executionId}`);
    console.log(`🎯 params.triggeredManually: ${params.triggeredManually}`);
    console.log(`🎯 params.featureId: ${params.featureId}`);
    console.log(`🎯 params.designChallenge preview: ${params.designChallenge?.substring(0, 100)}`);
    console.log("═══════════════════════════════════════════════════════════════");
    
    const { designChallenge, contextualRequirements } = params;

    // REGISTRY-DRIVEN: Use ModularWorkflowEngine if workflow has enabledFeatures
    const workflow = workflowRegistry.getWorkflow(workflowId);
    const usesModularEngine = workflow?.uiComponents?.enabledFeatures?.length > 0;
    
    console.log(`🎯 Workflow config:`, {
      workflowName: workflow?.name,
      isFeatureEnablement: workflow?.isFeatureEnablement,
      usesModularEngine,
      enabledFeaturesCount: workflow?.uiComponents?.enabledFeatures?.length
    });
    
    let executionPlan = null;
    
    // FIX #52: Respect explicit executionPlan from params (feature enablement)
    // When triggeredManually is true, use the params.executionPlan instead of deriving from registry
    // This prevents dual workflow bug where feature enablement restarts the entire plan creation
    if (params.executionPlan && params.triggeredManually) {
      executionPlan = params.executionPlan;
      console.log(`🎯 Using EXPLICIT executionPlan from params (manual feature enablement):`, {
        steps: executionPlan?.steps,
        skipSteps: executionPlan?.skipSteps,
        featureId: params.featureId,
        triggeredManually: params.triggeredManually
      });
    } else if (usesModularEngine) {
      const workflowConfig = await modularWorkflowEngine.createWorkflowConfig(workflowId, workflowRegistry);
      executionPlan = workflowConfig ? workflowConfig.executionPlan : null;
      
      console.log(`🎯 ModularWorkflowEngine: ${workflowId} execution plan:`, {
        requiredSteps: executionPlan?.steps,
        skipSteps: executionPlan?.skipSteps,
        estimatedDuration: workflowConfig?.estimatedDuration
      });
    } else {
      console.log(`🎯 Full autonomous workflow (${workflowId}): Running ALL steps (no modular engine)`);
    }

    // Create autonomous strategy service instance
    const autonomousService = new AutonomousStrategyWorkflowService();

    // Enhanced callbacks for autonomous workflow
    const enhancedCallbacks = {
      onStepUpdate: (stepIndex, step) => {
        const workflowState = this.activeWorkflows.get(executionId);
        if (workflowState) {
          workflowState.currentStep = stepIndex;
          workflowState.stepStatus = step.status;
        }

        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, step);
        }
      },

      onStepStart: async (stepIndex, step) => {
        console.log(
          `🚀 AutonomousWorkflow: Step ${stepIndex} starting:`,
          step.title,
        );

        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "running" });
        }

        if (stepIndex > 0 && callbacks?.onChatMessage) {
          // CRITICAL FIX: Pass featureId directly to get correct feature-specific steps
          // Without this, getWorkflowSteps falls back to 4-step autonomous workflow
          const updatedSteps = await this.getWorkflowSteps(workflowId, params.featureId);
          updatedSteps[stepIndex] = {
            ...updatedSteps[stepIndex],
            status: "running",
          };

          for (let i = 0; i < stepIndex; i++) {
            updatedSteps[i] = { ...updatedSteps[i], status: "completed" };
          }

          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: updatedSteps,
            currentStep: stepIndex,
            sender: "bot",
            autonomousMode: true,
            // CRITICAL FIX: Include featureId/progressId so FloatingChatbot can find and UPDATE
            // existing progress UI instead of adding new ones
            featureId: params.featureId,
            progressId: params.featureId || 'default',
            isFeatureEnablement: !!params.featureId,
          });
        }
      },

      onStepComplete: async (stepIndex, step, results) => {
        console.log(
          `✅ AutonomousWorkflow: Step ${stepIndex} completed:`,
          step.title,
        );

        if (callbacks?.onStepUpdate) {
          callbacks.onStepUpdate(stepIndex, { ...step, status: "completed" });
        }

        if (callbacks?.onChatMessage) {
          // CRITICAL FIX: Pass featureId directly to get correct feature-specific steps
          // Without this, getWorkflowSteps falls back to 4-step autonomous workflow
          const updatedSteps = await this.getWorkflowSteps(workflowId, params.featureId);
          updatedSteps[stepIndex] = {
            ...updatedSteps[stepIndex],
            status: "completed",
          };

          for (let i = 0; i <= stepIndex; i++) {
            if (updatedSteps[i].status !== "error") {
              updatedSteps[i] = { ...updatedSteps[i], status: "completed" };
            }
          }

          // FIX: Cap currentStep at last valid index to prevent empty step appearing
          // When last step completes, don't increment beyond the array bounds
          const nextStep = Math.min(stepIndex + 1, updatedSteps.length - 1);

          callbacks.onChatMessage({
            isProgressUIUpdate: true,
            workflowSteps: updatedSteps,
            currentStep: nextStep,
            sender: "bot",
            autonomousMode: true,
            // CRITICAL FIX: Include featureId/progressId so FloatingChatbot can find and UPDATE
            // existing progress UI instead of adding new ones
            featureId: params.featureId,
            progressId: params.featureId || 'default',
            isFeatureEnablement: !!params.featureId,
          });
        }
      },

      onComplete: async (workflowResults) => {
        console.log("🎉🎉🎉 COMPLETION PATH TRACE [1/4]: StrategyWorkflowManager.onComplete called");
        console.log("🎉 AutonomousWorkflow: Autonomous strategy completed");

        const processedResults =
          await this.processAutonomousWorkflowResults(workflowResults);

        // NOTE: "Strategy Complete" message is now injected via onSaveCompleteCallback in EmbeddedChatPanel
        // after the plan is saved (so it appears with the correct planId context).
        // Feature enablement modes (continueConversation/triggeredManually) never show this message.
        if (params.continueConversation || params.triggeredManually) {
          console.log("📝 Skipping post-strategy message - feature enablement mode");
        }

        if (callbacks?.onComplete) {
          const enhancedResults = {
            ...workflowResults,
            ...processedResults,
            isAutonomous: true,
            strategyType: "autonomous_contextual",
            // NOTE: onSaveComplete/onSaveError removed - conversation linking now handled directly in ComponentActionHandler
          };

          console.log("🎉🎉🎉 COMPLETION PATH TRACE [2/4]: StrategyWorkflowManager calling callbacks.onComplete");
          console.log("📦 Enhanced results being passed:", {
            dataKeys: Object.keys(enhancedResults)
          });
          callbacks.onComplete(enhancedResults);
        }

        return processedResults;
      },

      onError: (error) => {
        console.error("❌ AutonomousWorkflow: Workflow failed:", error);

        if (callbacks?.onChatMessage) {
          callbacks.onChatMessage({
            text: "⚠️ I encountered an issue while creating your autonomous strategy. Let me try a different approach or you can refresh to start over.",
            sender: "bot",
            isHtml: false,
          });
        }

        if (callbacks?.onError) {
          callbacks.onError(error);
        }
      },
    };

    // Send initial autonomous workflow messages
    // FIX: Skip initial messages for feature enablement (continueConversation=true) to prevent duplicates
    if (callbacks?.onChatMessage && !params.continueConversation) {
      const workflowState = this.activeWorkflows.get(executionId);

      if (workflowState && !workflowState.messagesInitialized) {
        console.log(
          `📝 AutonomousWorkflow: Initializing messages for execution ${executionId}`,
        );

        workflowState.messagesInitialized = true;
        this.activeWorkflows.set(executionId, workflowState);

        const messageQueue = [
          {
            text: designChallenge,
            sender: "user",
            isHtml: false,
          },
          {
            text: " Creating your design strategy using Pab'ss full AI & creative potential, unrestricted by templates...",
            sender: "bot",
            isHtml: false,
          },
        ];

        this.sendMessagesWithProgressUI(
          callbacks.onChatMessage,
          messageQueue,
          executionId,
          true, // autonomousMode flag
        );
      }
    } else if (params.continueConversation) {
      console.log(
        `⏭️ AutonomousWorkflow: Skipping initial messages for ${executionId} (continueConversation=true, feature enablement mode)`,
      );
    }

    // Execute the autonomous strategy workflow with execution plan
    console.log(`🚀🚀🚀 TRACE [F] About to call autonomousService.executeWorkflow for ${workflowId}`, {
      hasEnhancedCallbacks: !!enhancedCallbacks,
      hasOnComplete: !!enhancedCallbacks?.onComplete,
      hasExecutionPlan: !!executionPlan
    });
    return await autonomousService.executeWorkflow(
      designChallenge,
      contextualRequirements || {},
      enhancedCallbacks,
      params.csvData || null,
      executionPlan,
    );
  }

  // Process autonomous workflow results
  async processAutonomousWorkflowResults(workflowResults) {
    console.log("🔧 Processing autonomous workflow results");

    // Programs are optional for lightweight workflows (e.g., Decision Call)
    const programs =
      workflowResults.autonomousPrograms ||
      workflowResults.finalProgramSequence ||
      [];
    const calculatedTotalDuration = programs.reduce((sum, program) => {
      return (
        sum +
        (program.totalProgramLength ||
          program.duration_estimate ||
          program.duration ||
          40)
      );
    }, 0);

    const enhancedResults = {
      ...workflowResults,
      totalDuration: calculatedTotalDuration,
      metadata: {
        processedAt: new Date().toISOString(),
        programCount: programs.length,
        validationPassed: true,
        strategyType: "autonomous",
      },
      uiUpdate: {
        shortSummary: workflowResults.shortSummary,
        problemStatement: workflowResults.problemStatement,
        goalStatement: workflowResults.goalStatement,
        targetStatement: workflowResults.targetStatement,
        objectives: workflowResults.objectives,
        deliverables: workflowResults.deliverables,
        metrics: workflowResults.metrics,
        selectedPrograms: programs,
        finalProgramSequence: programs,
        stakeholders: workflowResults.stakeholders,
        totalDuration: calculatedTotalDuration,
        isAutonomous: true,
      },
    };

    return enhancedResults;
  }

  // Clean up stale workflows that may be stuck
  cleanupStaleWorkflows() {
    const now = new Date();
    const staleThreshold = 2 * 60 * 1000; // Reduced to 2 minutes

    for (const [executionId, workflow] of this.activeWorkflows.entries()) {
      if (
        workflow.status === "running" &&
        now - workflow.startTime > staleThreshold
      ) {
        console.log(`🧹 Cleaning up stale workflow: ${executionId}`, {
          age: (now - workflow.startTime) / 1000,
          status: workflow.status,
        });
        this.activeWorkflows.set(executionId, {
          ...workflow,
          status: "timeout",
          endTime: now,
        });
        this.workflowCallbacks.delete(executionId);
      }
    }
  }

  // ENHANCED: Send messages when UI is ready
  async sendMessagesWhenReady(onChatMessage, messageQueue, executionId) {
    console.log(
      `⏳ StrategyWorkflowManager: Waiting for UI readiness for execution ${executionId}`,
    );

    // Test if UI is ready by sending a test message
    let uiReady = false;
    let attempts = 0;
    const maxAttempts = 10;

    while (!uiReady && attempts < maxAttempts) {
      try {
        // Small delay to allow UI initialization
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Test message to check UI readiness
        const testResult = onChatMessage({
          test: true,
          sender: "system",
        });

        // If no error, assume UI is ready
        uiReady = true;
        console.log(`✅ UI ready after ${attempts + 1} attempts`);
      } catch (error) {
        attempts++;
        console.log(`⏳ UI not ready, attempt ${attempts}/${maxAttempts}`);

        if (attempts >= maxAttempts) {
          console.warn(
            `⚠️ UI readiness timeout after ${maxAttempts} attempts, sending messages anyway`,
          );
          uiReady = true; // Force send
        } else {
          // Exponential backoff
          await new Promise((resolve) => setTimeout(resolve, 200 * attempts));
        }
      }
    }

    // Send all queued messages
    console.log(`📤 Sending ${messageQueue.length} queued messages`);
    for (const message of messageQueue) {
      try {
        onChatMessage(message);
        // Small delay between messages for smooth UI updates
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error(`❌ Failed to send message:`, error);
      }
    }
  }

  // NEW: Send messages and progress UI in correct order with dynamic workflow detection
  // featureId is optional - only used for feature_enablement workflows
  async sendMessagesWithProgressUI(
    onChatMessage,
    messageQueue,
    executionId,
    autonomousMode = false,
    featureId = null,
  ) {
    console.log(
      `⏳ StrategyWorkflowManager: Sending messages and progress UI for execution ${executionId}`,
      { featureId, autonomousMode }
    );

    // Extract workflowId from executionId
    const parts = executionId.split('_');
    const workflowId = parts.slice(0, -1).join('_');
    
    // FEATURE ENABLEMENT: Use personalized messages instead of generic ones
    const workflow = workflowRegistry.getWorkflow(workflowId);
    if (workflow?.isFeatureEnablement && featureId) {
      const templates = workflowRegistry.getFeatureEnablementTemplates(featureId);
      console.log(`🎯 Feature enablement mode: Using personalized messages for ${featureId}`);
      
      // Override messageQueue with personalized intro message
      const personalizedQueue = [{
        text: templates.intro,
        sender: "bot",
        isHtml: false,
        isFeatureEnablement: true,
        featureId: featureId,
      }];
      
      // Send personalized intro
      await this.sendMessagesWhenReady(onChatMessage, personalizedQueue, executionId);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get feature-specific steps
      const initialSteps = await this.getWorkflowSteps(workflowId, featureId);
      initialSteps[0] = { ...initialSteps[0], status: "running" };
      
      console.log(`🎯 Sending feature enablement progress UI (${templates.name}, ${initialSteps.length} steps)`);
      onChatMessage({
        isProgressUI: true,
        workflowSteps: initialSteps,
        currentStep: 0,
        sender: "bot",
        autonomousMode: true,
        isFeatureEnablement: true,
        featureId: featureId,
        featureName: templates.name,
      });
      return;
    }

    // First send the messages using existing method
    await this.sendMessagesWhenReady(onChatMessage, messageQueue, executionId);

    // Add explicit delay to ensure messages are committed to chat UI before progress UI
    await new Promise(resolve => setTimeout(resolve, 100));

    // THEN send progress UI (no setTimeout wrapping - execute synchronously after messages)
    // Get workflow-specific steps based on mode
    let initialSteps;
    if (autonomousMode) {
      console.log(`🔍 Extracted workflowId from executionId: ${workflowId} from ${executionId}`);
      
      // For autonomous workflows (quick_plan or autonomous_design_strategy), get optimized steps
      initialSteps = await this.getWorkflowSteps(workflowId);
    } else {
      initialSteps = await this.getWorkflowSteps();
    }

    initialSteps[0] = { ...initialSteps[0], status: "running" };

    console.log(
      `🎯 Sending progress UI after messages (autonomous: ${autonomousMode}, steps: ${initialSteps.length})`,
    );
    onChatMessage({
      isProgressUI: true,
      workflowSteps: initialSteps,
      currentStep: 0,
      sender: "bot",
      autonomousMode: autonomousMode,
    });
  }

  // Helper: Send feature enablement completion message
  sendFeatureEnablementCompletion(onChatMessage, featureId) {
    const templates = workflowRegistry.getFeatureEnablementTemplates(featureId);
    console.log(`✅ Sending feature enablement completion for ${featureId}`);
    
    onChatMessage({
      text: templates.completion,
      sender: "bot",
      isHtml: false,
      isFeatureEnablementComplete: true,
      featureId: featureId,
    });
  }

  // NEW: Feature Workflow Sequencer - executes feature workflows in sequence
  // This is the new architecture where each feature owns its own workflow
  async executeSequencedFeatureWorkflows(executionId, workflowId, params, callbacks) {
    console.log("🔄🔄🔄 FEATURE SEQUENCER: Starting sequenced workflow execution 🔄🔄🔄");
    console.log(`🔄 Product: ${workflowId}, executionId: ${executionId}`);
    console.log(`🔄 callbacks present:`, {
      hasCallbacks: !!callbacks,
      hasOnComplete: !!callbacks?.onComplete,
      hasOnChatMessage: !!callbacks?.onChatMessage,
      hasOnError: !!callbacks?.onError,
    });
    
    try {

    const { designChallenge, csvData } = params;

    // CRITICAL FIX: Send initial messages (user challenge + bot intro) FIRST
    // This was missing - the user's message wasn't showing in chat!
    if (callbacks?.onChatMessage) {
      const workflowState = this.activeWorkflows.get(executionId);
      
      if (workflowState && !workflowState.messagesInitialized) {
        console.log(`📝 FEATURE SEQUENCER: Sending initial messages for ${executionId}`);
        
        workflowState.messagesInitialized = true;
        this.activeWorkflows.set(executionId, workflowState);
        
        // Send user message first
        callbacks.onChatMessage({
          text: designChallenge,
          sender: "user",
          isHtml: false,
        });
        
        // Small delay for smooth UI
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Send bot intro message
        callbacks.onChatMessage({
          text: "🚀 Creating your design strategy...",
          sender: "bot",
          isHtml: false,
        });
        
        // Small delay before progress UI
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Get the feature workflows to run for this product
    const featureWorkflows = workflowRegistry.getInitialPlanWorkflows(workflowId);
    
    if (featureWorkflows.length === 0) {
      console.error(`❌ No feature workflows found for ${workflowId}`);
      throw new Error(`No feature workflows configured for ${workflowId}`);
    }

    console.log(`🔄 Will execute ${featureWorkflows.length} feature workflows:`, 
      featureWorkflows.map(fw => fw.featureId));

    // Build combined steps for progress UI
    let allSteps = [];
    let stepOffset = 0;
    const featureStepMapping = [];

    for (const fw of featureWorkflows) {
      const featureSteps = fw.steps.map((step, idx) => ({
        ...step,
        id: `${fw.featureId}_${step.id}`,
        featureId: fw.featureId,
        globalIndex: stepOffset + idx,
        localIndex: idx,
      }));
      allSteps = allSteps.concat(featureSteps);
      featureStepMapping.push({
        featureId: fw.featureId,
        startIndex: stepOffset,
        stepCount: fw.steps.length,
      });
      stepOffset += fw.steps.length;
    }

    console.log(`🔄 Combined progress UI: ${allSteps.length} total steps`);

    // Update workflow state with sequenced info
    const workflowState = this.activeWorkflows.get(executionId);
    if (workflowState) {
      workflowState.isSequencedWorkflow = true;
      workflowState.totalSteps = allSteps.length;
      workflowState.featureStepMapping = featureStepMapping;
      this.activeWorkflows.set(executionId, workflowState);
    }

    // Send initial progress UI
    if (callbacks?.onChatMessage) {
      const initialSteps = allSteps.map((step, idx) => ({
        ...step,
        status: idx === 0 ? "running" : "pending",
      }));

      callbacks.onChatMessage({
        isProgressUI: true,
        workflowSteps: initialSteps,
        currentStep: 0,
        sender: "bot",
        autonomousMode: true,
        isSequencedWorkflow: true,
      });
    }

    // Execute each feature workflow in sequence
    const results = {};
    let accumulatedResults = {}; // Accumulate all feature results by feature ID

    console.log("🔥🔥🔥 SEQUENCER FOR LOOP: Starting feature workflow execution loop");
    console.log(`🔥🔥🔥 SEQUENCER FOR LOOP: Total features to execute: ${featureWorkflows.length}`);

    for (let fwIndex = 0; fwIndex < featureWorkflows.length; fwIndex++) {
      const fw = featureWorkflows[fwIndex];
      const featureMapping = featureStepMapping[fwIndex];

      console.log(`🔥🔥🔥 SEQUENCER FOR LOOP [${fwIndex + 1}/${featureWorkflows.length}]: Starting ${fw.featureId}`);
      console.log(`🔄 Executing feature workflow: ${fw.featureId}`);

      // Build params for this feature's workflow
      // Pass accumulated results so each feature can access any previous feature's output
      const featureParams = {
        designChallenge,
        csvData,
        briefData: accumulatedResults.brief || null, // Always pass Brief result specifically
        strategyData: accumulatedResults.product_strategy || null, // Pass Product Strategy result
        accumulatedResults, // Pass all accumulated results for flexibility
        enabledFeatures: params.enabledFeatures || [], // Pass enabled features for smart recommendations
      };

      // Create callbacks that map to global progress
      const featureCallbacks = {
        onStepStart: (localIndex, step) => {
          const globalIndex = featureMapping.startIndex + localIndex;
          console.log(`🚀 [${fw.featureId}] Step ${localIndex} starting: ${step.title} (global: ${globalIndex})`);

          // Update active workflow state
          const currentWorkflowState = this.activeWorkflows.get(executionId);
          if (currentWorkflowState) {
            currentWorkflowState.currentStep = globalIndex;
            currentWorkflowState.currentFeature = fw.featureId;
            currentWorkflowState.stepStatus = "running";
            this.activeWorkflows.set(executionId, currentWorkflowState);
          }

          if (callbacks?.onChatMessage) {
            const updatedSteps = allSteps.map((s, idx) => ({
              ...s,
              status: idx < globalIndex ? "completed" : idx === globalIndex ? "running" : "pending",
            }));

            callbacks.onChatMessage({
              isProgressUIUpdate: true,
              workflowSteps: updatedSteps,
              currentStep: globalIndex,
              sender: "bot",
              autonomousMode: true,
              isSequencedWorkflow: true,
            });
          }
        },

        onStepComplete: (localIndex, step, stepResult) => {
          const globalIndex = featureMapping.startIndex + localIndex;
          console.log(`✅ [${fw.featureId}] Step ${localIndex} completed: ${step.title} (global: ${globalIndex})`);

          // Update active workflow state
          const currentWorkflowState = this.activeWorkflows.get(executionId);
          if (currentWorkflowState) {
            currentWorkflowState.currentStep = globalIndex;
            currentWorkflowState.stepStatus = "completed";
            this.activeWorkflows.set(executionId, currentWorkflowState);
          }

          if (callbacks?.onChatMessage) {
            const nextStep = Math.min(globalIndex + 1, allSteps.length - 1);
            const updatedSteps = allSteps.map((s, idx) => ({
              ...s,
              status: idx <= globalIndex ? "completed" : idx === nextStep ? "running" : "pending",
            }));

            callbacks.onChatMessage({
              isProgressUIUpdate: true,
              workflowSteps: updatedSteps,
              currentStep: nextStep,
              sender: "bot",
              autonomousMode: true,
              isSequencedWorkflow: true,
            });
          }
        },

        onComplete: (featureResult) => {
          console.log(`✅ [${fw.featureId}] Feature workflow completed`);
          results[fw.featureId] = featureResult;
          accumulatedResults[fw.featureId] = featureResult; // Accumulate by feature ID
        },

        onError: (error) => {
          console.error(`❌ [${fw.featureId}] Feature workflow failed:`, error);
          if (callbacks?.onError) {
            callbacks.onError(error);
          }
        },
      };

      // Execute this feature's workflow
      try {
        console.log(`🔥🔥🔥 SEQUENCER AWAIT [${fwIndex + 1}/${featureWorkflows.length}]: About to await ${fw.featureId}.executeWorkflow()`);
        const workflowResult = await fw.service.executeWorkflow(featureParams, featureCallbacks);
        console.log(`🔥🔥🔥 SEQUENCER AWAIT [${fwIndex + 1}/${featureWorkflows.length}]: ${fw.featureId}.executeWorkflow() RETURNED`);
        console.log(`🔥🔥🔥 SEQUENCER AWAIT [${fwIndex + 1}/${featureWorkflows.length}]: ${fw.featureId} result keys:`, Object.keys(workflowResult || {}));
        
        // CRITICAL FIX: Also capture result directly from await return value
        // This ensures we get results even if onComplete callback timing is off
        if (workflowResult && !results[fw.featureId]) {
          console.log(`🔥🔥🔥 SEQUENCER FIX: Capturing ${fw.featureId} result from await return (was not set by onComplete)`);
          results[fw.featureId] = workflowResult;
          accumulatedResults[fw.featureId] = workflowResult; // Accumulate by feature ID
        }
      } catch (error) {
        console.error(`❌ Feature workflow ${fw.featureId} failed:`, error);
        console.error(`❌ Feature workflow ${fw.featureId} error stack:`, error.stack);
        throw error;
      }
      
      console.log(`🔥🔥🔥 SEQUENCER FOR LOOP [${fwIndex + 1}/${featureWorkflows.length}]: Completed ${fw.featureId}`);
      console.log(`🔥🔥🔥 SEQUENCER FOR LOOP [${fwIndex + 1}/${featureWorkflows.length}]: Current results keys:`, Object.keys(results));
    }

    console.log("🔥🔥🔥 SEQUENCER FOR LOOP: Exited loop successfully");
    console.log("🔥🔥🔥 SEQUENCER FOR LOOP: Final results keys:", Object.keys(results));

    // Compile final results from all features
    console.log("🔥🔥🔥 SEQUENCER: About to call compileSequencedResults");
    const finalResults = this.compileSequencedResults(results, featureWorkflows);
    console.log("🔥🔥🔥 SEQUENCER: compileSequencedResults returned");

    console.log("🎉 FEATURE SEQUENCER: All workflows completed successfully");

    // Call the completion callback
    console.log("🔄 FEATURE SEQUENCER: About to call callbacks.onComplete");
    if (callbacks?.onComplete) {
      console.log("🔄 FEATURE SEQUENCER: Calling callbacks.onComplete with finalResults");
      callbacks.onComplete(finalResults);
      console.log("🔄 FEATURE SEQUENCER: callbacks.onComplete returned");
    } else {
      console.error("❌ FEATURE SEQUENCER: callbacks.onComplete is missing!");
    }

    return finalResults;
    
    } catch (error) {
      console.error("❌❌❌ FEATURE SEQUENCER CRITICAL ERROR:", error);
      console.error("❌❌❌ Error stack:", error.stack);
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
      throw error;
    }
  }

  // Compile results from sequenced feature workflows
  compileSequencedResults(results, featureWorkflows) {
    console.log("🔧 Compiling sequenced workflow results");
    console.log("🔧 Input results keys:", Object.keys(results));

    const briefResult = results.brief || {};
    const actionPlanResult = results.action_plan || {};
    const productStrategyResult = results.product_strategy || {};

    console.log("🔧 Feature results extracted:", {
      hasBrief: Object.keys(briefResult).length > 0,
      hasActionPlan: Object.keys(actionPlanResult).length > 0,
      hasProductStrategy: Object.keys(productStrategyResult).length > 0,
    });

    // Merge results from all features
    const compiledResults = {
      // Brief data
      planName: briefResult.planName || "",
      shortSummary: briefResult.shortSummary || "",
      problemStatement: briefResult.problemStatement || "",
      goalStatement: briefResult.goalStatement || "",
      targetStatement: briefResult.targetStatement || "",
      objectives: briefResult.objectives || [],
      deliverables: briefResult.deliverables || [],
      metrics: briefResult.metrics || [],
      stakeholders: briefResult.stakeholders || [],
      contextualFactors: briefResult.contextualFactors || [],
      domainInsights: briefResult.domainInsights || [],

      // Product Strategy data (if included) - CRITICAL: Add this for Decision Call
      // Note: experienceDesignPrinciples comes from Product Strategy, not Brief
      experienceDesignPrinciples: productStrategyResult.experienceDesignPrinciples || briefResult.experienceDesignPrinciples || "",
      designHypotheses: productStrategyResult.designHypotheses || [],
      designHypothesis: productStrategyResult.designHypothesis || [],
      customerGoals: productStrategyResult.customerGoals || [],
      painPoints: productStrategyResult.painPoints || [],
      unmetNeeds: productStrategyResult.unmetNeeds || [],
      designVision: productStrategyResult.designVision || "",
      competitivePositioning: productStrategyResult.competitivePositioning || "",
      competitiveUxPositioning: productStrategyResult.competitiveUxPositioning || "",
      successMetrics: productStrategyResult.successMetrics || [],
      strategicThemes: productStrategyResult.strategicThemes || [],
      opportunityAreas: productStrategyResult.opportunityAreas || [],
      strategicDeliverables: productStrategyResult.strategicDeliverables || [],
      informationAssessment: productStrategyResult.informationAssessment || {},
      strategicGuidance: productStrategyResult.strategicGuidance || {},
      audienceInsights: productStrategyResult.audienceInsights || [],
      briefDeconstruction: productStrategyResult.briefDeconstruction || {},

      // Action Plan data (if included)
      finalProgramSequence: actionPlanResult.finalProgramSequence || [],
      selectedPrograms: actionPlanResult.selectedPrograms || [],
      autonomousPrograms: actionPlanResult.autonomousPrograms || [],
      interconnectedTools: actionPlanResult.interconnectedTools || [],
      researchApproach: actionPlanResult.researchApproach || [],
      dataCollection: actionPlanResult.dataCollection || [],
      totalDuration: actionPlanResult.totalDuration || 0,

      // Metadata
      isAutonomous: true,
      isSequencedWorkflow: true,
      // CRITICAL FIX: Determine workflow type based on whether action_plan was executed, not feature count
      workflowType: featureWorkflows.some(fw => fw.featureId === 'action_plan') ? "full_plan" : "quick_plan",
      featureResults: results,
      metadata: {
        generatedAt: new Date().toISOString(),
        featuresExecuted: featureWorkflows.map(fw => fw.featureId),
      },
      // CRITICAL FIX: Add workflowMetadata with customProgramsSkipped flag for Decision Call
      // This tells handleStrategyCreationComplete that empty programs are expected (not an error)
      workflowMetadata: {
        executionPlan: {
          customProgramsSkipped: !featureWorkflows.some(fw => fw.featureId === 'action_plan'),
        },
      },
    };

    // Validate Brief data exists
    if (!compiledResults.objectives || compiledResults.objectives.length === 0) {
      console.warn("⚠️ No objectives in compiled results");
    }

    // For Decision Call, programs may be empty - that's OK
    if (featureWorkflows.some(fw => fw.featureId === 'action_plan')) {
      if (!compiledResults.finalProgramSequence || compiledResults.finalProgramSequence.length === 0) {
        console.warn("⚠️ No programs in compiled results but action_plan was executed");
      }
    }

    console.log("✅ Compiled results:", {
      hasObjectives: compiledResults.objectives?.length > 0,
      hasPrograms: compiledResults.finalProgramSequence?.length > 0,
      totalDuration: compiledResults.totalDuration,
      featuresExecuted: compiledResults.metadata.featuresExecuted,
    });

    return compiledResults;
  }

  // Get combined steps for a product's feature workflows (for progress UI)
  async getSequencedWorkflowSteps(workflowId) {
    const featureWorkflows = workflowRegistry.getInitialPlanWorkflows(workflowId);
    
    let allSteps = [];
    for (const fw of featureWorkflows) {
      const featureSteps = fw.steps.map((step, idx) => ({
        ...step,
        id: `${fw.featureId}_${step.id}`,
        title: step.title,
        description: step.description,
        featureId: fw.featureId,
        status: "pending",
      }));
      allSteps = allSteps.concat(featureSteps);
    }

    return allSteps;
  }

}

// Export singleton instance
const strategyWorkflowManager = new StrategyWorkflowManager();

export default strategyWorkflowManager;
export { StrategyWorkflowManager };
